import { type ShippingAddress, type UserAttributes } from '../types.ts';
import { compact, toStr } from './helpers.ts';
import {
    DEFAULT_CURRENCY_CODE,
    type ElevarAuthUserProperties,
    type ElevarCustomerAttributes,
    type ElevarUserProperties,
} from './types.ts';

let cachedUserProperties: ElevarUserProperties = { visitor_type: 'guest' };
let cachedUserCurrency: string | undefined;

let pushedAuthEvents: { dl_login: boolean; dl_sign_up: boolean } = {
    dl_login: false,
    dl_sign_up: false,
};

const mapElevarCustomerFields = (
    attrs: ElevarCustomerAttributes
): Omit<ElevarUserProperties, 'visitor_type' | 'customer_id'> =>
    compact({
        customer_email: toStr(attrs.customer_email),
        customer_first_name: toStr(attrs.customer_first_name),
        customer_last_name: toStr(attrs.customer_last_name),
        customer_phone: toStr(attrs.customer_phone),
        customer_address_1: toStr(attrs.customer_address_1),
        customer_address_2: toStr(attrs.customer_address_2),
        customer_city: toStr(attrs.customer_city),
        customer_province: toStr(attrs.customer_province),
        customer_province_code: toStr(attrs.customer_province_code),
        customer_zip: toStr(attrs.customer_zip),
        customer_country: toStr(attrs.customer_country),
        customer_order_count: toStr(attrs.customer_order_count),
    });

export const setElevarUserProperties = (attrs: UserAttributes): ElevarUserProperties => {
    cachedUserCurrency = toStr(attrs.defaultCurrency);
    cachedUserProperties = compact({
        visitor_type: attrs.visitor_type === 'logged_in' ? 'logged_in' : 'guest',
        customer_id: toStr(attrs.userID),
        ...mapElevarCustomerFields(attrs),
    });
    return cachedUserProperties;
};

export const getElevarUserProperties = (): ElevarUserProperties => cachedUserProperties;

export const mapAddressToElevarCustomerFields = (
    address: ShippingAddress
): Omit<ElevarUserProperties, 'visitor_type' | 'customer_id'> =>
    mapElevarCustomerFields({
        customer_first_name: toStr(address.firstName),
        customer_last_name: toStr(address.lastName),
        customer_phone: toStr(address.phoneNumber),
        customer_address_1: toStr(address.line1),
        customer_address_2: toStr(address.line2),
        customer_city: toStr(address.city),
        customer_province: toStr(address.displayState) ?? toStr(address.state),
        customer_province_code: toStr(address.state),
        customer_zip: toStr(address.zipCode),
        customer_country: toStr(address.country),
    });

export const patchElevarUserPropertiesFromAddress = (address: ShippingAddress): void => {
    cachedUserProperties = compact({
        ...cachedUserProperties,
        ...mapAddressToElevarCustomerFields(address),
    });
};

export const getElevarCurrencyCode = (): string => cachedUserCurrency ?? DEFAULT_CURRENCY_CODE;

export const getElevarAuthUserProperties = (): ElevarAuthUserProperties | null => {
    const { customer_email: customerEmail } = cachedUserProperties;
    if (!customerEmail) {
        return null;
    }
    return { ...cachedUserProperties, visitor_type: 'logged_in', customer_email: customerEmail };
};

export const setElevarAuthUser = ({
    email,
    customerId,
}: {
    email: string | null | undefined;
    customerId?: string | null;
}): void => {
    const customerEmail = toStr(email);
    if (!customerEmail) {
        return;
    }
    cachedUserProperties = compact({
        ...cachedUserProperties,
        visitor_type: 'logged_in',
        customer_email: customerEmail,
        customer_id: toStr(customerId) ?? cachedUserProperties.customer_id,
    });
};

export const hasPushedElevarAuthEvent = (event: 'dl_login' | 'dl_sign_up'): boolean =>
    pushedAuthEvents[event];

export const markElevarAuthEventPushed = (event: 'dl_login' | 'dl_sign_up'): void => {
    pushedAuthEvents[event] = true;
};

export const resetElevarUserProperties = (): void => {
    cachedUserProperties = { visitor_type: 'guest' };
    pushedAuthEvents = { dl_login: false, dl_sign_up: false };
    cachedUserCurrency = undefined;
};
